import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { AlertUnsavedData, Modal, ModalSize, NotificationToaster } from "components/notifications";
import { FormTextInput, FormSwitch } from "components/form-fields";
import { Button, ButtonSave } from "components/buttons";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PcnService } from "services/PcnService";
import { Intent, Alignment } from "@blueprintjs/core";

// Column length mirrors the SQL NVARCHAR limit enforced server-side.
const contraventionReasonMaxLength = 255;
const badRequestCode = 400;

const defaultModel = {
    id: 0,
    contraventionReason: "",
    timeRequired: false
};

export function PcnReasonForContraventionModal(
    {
        showModal,
        modalTitle,
        isUpdate = false,
        loading = false,
        onSave,
        onCloseModal,
        contraventionReasonDetails = defaultModel
    }
) {
    const [original, setOriginal] = useState(defaultModel);
    const [current, setCurrent] = useState(defaultModel);
    const [saving, setSaving] = useState(false);
    const [blockModalClose, setBlockModalClose] = useState(false);
    const isDirty = useDirtyData(original, current);
    const [isValid, errors, validate] = useValidation();

    useEffect(loadDetails, [contraventionReasonDetails, showModal]);

    function loadDetails() {
        if (!showModal) {
            return;
        }

        const mapped = {
            id: contraventionReasonDetails.id || defaultModel.id,
            contraventionReason: contraventionReasonDetails.contraventionReason || defaultModel.contraventionReason,
            timeRequired: contraventionReasonDetails.timeRequired ?? defaultModel.timeRequired
        };

        setOriginal(mapped);
        setCurrent(mapped);
    }

    function onFieldChange(fieldName, passedValue) {
        setCurrent({
            ...current,
            [fieldName]: passedValue
        });
    }

    useEffect(runValidation, [current, showModal, loading]);

    function runValidation() {
        if (!showModal || loading) {
            return;
        }

        const rules = [
            { fieldName: "contraventionReason", required: true, maxLength: contraventionReasonMaxLength }
        ];

        validate(rules, current);
    }

    function onSaveClick() {
        if (!isValid || !isDirty || saving || loading) {
            return;
        }

        const request = {
            contraventionReason: current.contraventionReason,
            timeRequired: current.timeRequired
        };

        setSaving(true);

        const savePromise = isUpdate
            ? PcnService.updateContraventionReason(current.id, request)
            : PcnService.createContraventionReason(request);

        savePromise
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, isUpdate ? "Contravention reason updated successfully." : "Contravention reason created successfully.");
                onSaveComplete();
            })
            .catch((error) => {
                if (error?.response?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Could not save the contravention reason. Please try again.");
                }
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function onSaveComplete() {
        resetModal();
        onSave();
    }

    function onCloseClick(forceClose = false) {
        if (isDirty && !forceClose) {
            setBlockModalClose(true);
            return;
        }

        resetModal();

        if (onCloseModal) {
            onCloseModal();
        }
    }

    function resetModal() {
        setBlockModalClose(false);
        setOriginal(defaultModel);
        setCurrent(defaultModel);
    }

    return (
        <Fragment>
            <Modal
                isOpen={showModal}
                onClose={() => onCloseClick(false)}
                clickOutsideClose={true}
                title={modalTitle}
                size={ModalSize.MEDIUM}
                updateStateOnClose={false}
                footer={(
                    <div className="bp6-dialog-footer-actions">
                        <Button
                            text="Cancel"
                            intent={Intent.SECONDARY}
                            onClick={() => onCloseClick(true)}
                        />
                        <ButtonSave
                            id="save-contravention-reason-button"
                            onClick={onSaveClick}
                            simpleDisabled={!isValid || loading || !isDirty}
                            disabled={saving}
                            text={"Save"}
                        />
                    </div>
                )}
            >
                <FormTextInput
                    id="input-contravention-reason"
                    disabled={saving}
                    loading={loading}
                    value={current.contraventionReason}
                    headingText="Contravention Reason"
                    placeholder="Please type the contravention reason here"
                    onChange={(event) => onFieldChange("contraventionReason", event.target.value)}
                    dangerHelperText={errors.contraventionReason}
                    maxLength={contraventionReasonMaxLength}
                    large={true}
                    fill={true}
                />

                <FormSwitch
                    id="switch-time-required"
                    disabled={saving}
                    loading={loading}
                    checked={current.timeRequired}
                    labelElement="Time Required:"
                    inline={true}
                    alignment={Alignment.END}
                    onChange={(event) => onFieldChange("timeRequired", event.target.checked)}
                />
            </Modal>

            <AlertUnsavedData
                isDirty={isDirty}
                isDirtySamePage={blockModalClose}
                onCancelLeave={() => setBlockModalClose(false)}
                onConfirmLeave={() => onCloseClick(true)}
            />
        </Fragment>
    );
}

PcnReasonForContraventionModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    modalTitle: PropTypes.string.isRequired,
    isUpdate: PropTypes.bool,
    loading: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    contraventionReasonDetails: PropTypes.object
};
